import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { forwardRef, useState, useCallback, useMemo } from 'react';
import { useFloatingNodeId, useFloatingParentNodeId, useFloating, offset, flip, autoUpdate, useFloatingTree, useHover, safePolygon, useClick, useRole, useDismiss, useListNavigation, useTypeahead, useInteractions, useListItem, useMergeRefs, FloatingNode, FloatingList, FloatingPortal, FloatingFocusManager, FloatingTree } from '@floating-ui/react';
import classnames from 'classnames';
import { useConfigContextLoose, Button, Flex } from '@cognite/cogs-core';
import { ChevronRightSmallIcon } from '@cognite/cogs-icons';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { MenuItemAction, MenuItemToggled, MenuSection, MenuDivider, MenuItemLink } from './elements.js';
import { useMenuContext, MenuContext } from './menu-context.js';

var FloatingMenuCompound = /*#__PURE__*/forwardRef(function (_a, forwardedRef) {
  var className = _a.className,
    container = _a.container,
    label = _a.label,
    renderTrigger = _a.renderTrigger,
    _a$initialIsOpen = _a.initialIsOpen,
    initialIsOpen = _a$initialIsOpen === undefined ? false : _a$initialIsOpen,
    _a$placement = _a.placement,
    placement = _a$placement === undefined ? 'bottom-start' : _a$placement,
    open = _a.open,
    onOpenChange = _a.onOpenChange,
    _a$size = _a.size,
    size = _a$size === undefined ? 'small' : _a$size,
    floatingProps = _a.floatingProps,
    themeProps = _a.themeProps,
    triggerContainerClassName = _a.triggerContainerClassName,
    triggerContainerRef = _a.triggerContainerRef,
    disableCloseOnClickInside = _a.disableCloseOnClickInside,
    maxWidth = _a.maxWidth,
    minWidth = _a.minWidth,
    dataTestId = _a['data-testid'],
    rest = __rest(_a, ["className", "container", "label", "renderTrigger", "initialIsOpen", "placement", "open", "onOpenChange", "size", "floatingProps", "themeProps", "triggerContainerClassName", "triggerContainerRef", "disableCloseOnClickInside", "maxWidth", "minWidth", 'data-testid']);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    hasFocusInside = _React$useState2[0],
    setHasFocusInside = _React$useState2[1];
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    activeIndex = _React$useState4[0],
    setActiveIndex = _React$useState4[1];
  var elementsRef = React.useRef([]);
  var labelsRef = React.useRef([]);
  var parent = useMenuContext();
  var nodeId = useFloatingNodeId();
  // Determine if the component is in controlled mode
  var isControlled = typeof open !== 'undefined';
  var _useState = useState(initialIsOpen),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var openState = isControlled ? open : isOpen;
  var parentId = useFloatingParentNodeId();
  var isNested = parentId != null;
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  var handleOpenChange = useCallback(function (nextState) {
    if (!isControlled) {
      setIsOpen(nextState);
    }
    onOpenChange === null || onOpenChange === undefined ? undefined : onOpenChange(nextState);
  }, [isControlled, onOpenChange]);
  var _useFloating = useFloating(Object.assign({
      nodeId: nodeId,
      placement: isNested ? 'right-start' : placement,
      open: openState,
      onOpenChange: handleOpenChange,
      middleware: [offset({
        mainAxis: 4,
        alignmentAxis: isNested ? -4 : 0
      }), flip()].concat(_toConsumableArray((floatingProps === null || floatingProps === undefined ? undefined : floatingProps.middleware) || [])),
      // This make sure that the submenu is conditionally updated
      whileElementsMounted: autoUpdate
    }, floatingProps)),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    context = _useFloating.context;
  // Gives the access to tree object
  var tree = useFloatingTree();
  var hover = useHover(context, {
    enabled: isNested,
    delay: {
      open: 75
    },
    // This is needed to prevent the menu from closing when hovering over the submenu
    handleClose: safePolygon({
      blockPointerEvents: true
    })
  });
  var click = useClick(context, {
    event: 'mousedown',
    toggle: !isNested,
    ignoreMouse: isNested
  });
  var role = useRole(context, {
    role: 'menu'
  });
  // This is needed when we press the escape key to close the menu
  var dismiss = useDismiss(context, {
    bubbles: true
  });
  var listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex: activeIndex,
    nested: isNested,
    onNavigate: setActiveIndex
  });
  var typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex: activeIndex
  });
  var _useInteractions = useInteractions([hover, click, role, dismiss, listNavigation, typeahead]),
    getReferenceProps = _useInteractions.getReferenceProps,
    getFloatingProps = _useInteractions.getFloatingProps,
    getItemProps = _useInteractions.getItemProps;
  var item = useListItem();
  var combinedRefs = useMergeRefs([refs.setReference, item.ref, forwardedRef]);
  var menuContextValue = useMemo(function () {
    return {
      activeIndex: activeIndex,
      setActiveIndex: setActiveIndex,
      getItemProps: getItemProps,
      setHasFocusInside: setHasFocusInside,
      isOpen: openState,
      disableCloseOnClickInside: disableCloseOnClickInside
    };
  }, [activeIndex, disableCloseOnClickInside, getItemProps, openState]);
  // Event emitter allows you to communicate across tree components.
  // This effect closes all menus when an item gets clicked anywhere
  // in the tree.
  React.useEffect(function () {
    if (!tree) return;
    function handleTreeClick() {
      handleOpenChange(false);
    }
    function onSubMenuOpen(event) {
      if (event.nodeId !== nodeId && event.parentId === parentId) {
        handleOpenChange(false);
      }
    }
    tree.events.on('click', handleTreeClick);
    tree.events.on('menuopen', onSubMenuOpen);
    return function () {
      tree.events.off('click', handleTreeClick);
      tree.events.off('menuopen', onSubMenuOpen);
    };
  }, [tree, nodeId, parentId, handleOpenChange]);
  var extendedClassName = classnames.apply(undefined, [className, 'cogs-lab', 'cogs-lab-menu'].concat(_toConsumableArray(getThemeClassNames(undefined, themeProps)), ["cogs-lab-menu--size-".concat(size)]));
  return jsxs(FloatingNode, {
    id: nodeId,
    children: [jsxs("div", {
      ref: triggerContainerRef,
      className: classnames.apply(undefined, [triggerContainerClassName].concat(_toConsumableArray(getThemeClassNames(undefined, themeProps)))),
      children: [renderTrigger && !isNested && renderTrigger(Object.assign({
        ref: combinedRefs,
        open: openState
      }, getReferenceProps(parent.getItemProps(Object.assign(Object.assign({}, rest), {
        onFocus: function onFocus(event) {
          var _a;
          (_a = rest.onFocus) === null || _a === undefined ? undefined : _a.call(rest, event);
          setHasFocusInside(false);
          parent.setHasFocusInside(true);
        }
      }))))), !renderTrigger && !isNested && jsx(Button, Object.assign({
        ref: combinedRefs
      }, getReferenceProps(parent.getItemProps(Object.assign(Object.assign({}, rest), {
        onFocus: function onFocus(event) {
          var _a;
          (_a = rest.onFocus) === null || _a === undefined ? undefined : _a.call(rest, event);
          setHasFocusInside(false);
          parent.setHasFocusInside(true);
        }
      }))), {
        children: label
      })), isNested && jsxs("li", Object.assign({
        ref: combinedRefs,
        tabIndex: parent.activeIndex === item.index ? 0 : -1,
        "data-open": openState ? '' : undefined,
        "data-focus-inside": hasFocusInside ? '' : undefined,
        role: isNested ? 'menuitem' : undefined,
        className: classnames.apply(undefined, ['cogs-lab cogs-lab-menu__item cogs-lab-menu__item__action cogs-lab-menu__item__submenu'].concat(_toConsumableArray(getThemeClassNames(undefined, themeProps))))
      }, getReferenceProps(parent.getItemProps(Object.assign(Object.assign({}, rest), {
        onFocus: function onFocus(event) {
          var _a;
          (_a = rest.onFocus) === null || _a === undefined ? undefined : _a.call(rest, event);
          setHasFocusInside(false);
          parent.setHasFocusInside(true);
        }
      }))), {
        children: [jsx(Flex, {
          flex: 1,
          children: label
        }), jsx(Flex, {
          className: "cogs-lab cogs-lab-menu__submenu__item__icon",
          children: isNested && jsx(ChevronRightSmallIcon, {})
        })]
      }))]
    }), jsx(MenuContext.Provider, {
      value: menuContextValue,
      children: jsx(FloatingList, {
        elementsRef: elementsRef,
        labelsRef: labelsRef,
        children: openState && jsx(FloatingPortal, {
          root: container !== null && container !== undefined ? container : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer(),
          children: jsx(FloatingFocusManager, {
            context: context,
            modal: false,
            disabled: true,
            initialFocus: isNested ? 0 : -1,
            returnFocus: false,
            children: jsx("ul", Object.assign({
              ref: refs.setFloating,
              className: extendedClassName,
              style: Object.assign(Object.assign({}, floatingStyles), {
                maxWidth: maxWidth,
                minWidth: minWidth
              })
            }, getFloatingProps(), {
              "data-testid": dataTestId,
              children: rest.children
            }))
          })
        })
      })
    })]
  });
});
var MenuInner = /*#__PURE__*/forwardRef(function (props, ref) {
  var parentId = useFloatingParentNodeId();
  if (parentId === null) {
    return jsx(FloatingTree, {
      children: jsx(FloatingMenuCompound, Object.assign({}, props, {
        ref: ref
      }))
    });
  }
  return jsx(FloatingMenuCompound, Object.assign({}, props, {
    ref: ref
  }));
});
var Menu = Object.assign(MenuInner, {
  ItemAction: MenuItemAction,
  ItemToggled: MenuItemToggled,
  Section: MenuSection,
  Divider: MenuDivider,
  ItemLink: MenuItemLink
});

export { FloatingMenuCompound, Menu };
